import { useState, useEffect, useCallback } from "react";

const API = "https://api.colourcount.co.uk";

async function api(path, opts = {}, token = null) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, { ...opts, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `API error ${res.status}`);
  }
  return res.json();
}

const PRESET_COLOURS = [
  "#1a1a1a","#3d2314","#5c3a1e","#7a5230","#8c6e3a","#b8943e","#d4b86a","#e8d49a",
  "#6b5a48","#8a7a5a","#a89878","#c4b898","#7a5a18","#9a7820","#b89428","#d4b040",
  "#8a4420","#b05828","#c87030","#d06020","#8a2830","#a83038","#c02028","#c04840",
  "#4a2858","#5a3068","#c8a8d0","#2040a0","#e8c820","#c82020","#6020a0",
  "#f5f0e8","#eee8d8","#c8d8f0","#d8c8e8","#d8cce0","#c8c8c0","#f0ece4","#e85d24","#888888",
];

function getContrastText(hex) {
  try {
    const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
    return (0.299*r+0.587*g+0.114*b)/255 > 0.45 ? "#1a1a1a" : "#ffffff";
  } catch { return "#1a1a1a"; }
}

function formatDate(ts) {
  return new Date(ts).toLocaleDateString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"});
}

// ---- AUTH SCREEN ----
function AuthScreen({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [salonName, setSalonName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit() {
    setError(""); setLoading(true);
    try {
      if (mode === "signup") {
        if (!salonName.trim()) { setError("Salon name required"); setLoading(false); return; }
        const data = await api("/api/signup", { method: "POST", body: JSON.stringify({ email, password, salon_name: salonName }) });
        onLogin(data.token, data.salon_name);
      } else {
        const data = await api("/api/login", { method: "POST", body: JSON.stringify({ email, password }) });
        onLogin(data.token, data.salon_name);
      }
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  const inp = { width:"100%",padding:"12px 14px",border:"1px solid #ddd",borderRadius:10,fontSize:15,fontFamily:"inherit",outline:"none",boxSizing:"border-box",background:"#fafafa" };

  return (
    <div style={{fontFamily:"'DM Sans',sans-serif",maxWidth:400,margin:"0 auto",padding:"60px 20px",color:"#1a1a1a",background:"#ffffff",minHeight:"100vh"}}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet"/>
      <div style={{textAlign:"center",marginBottom:40}}>
        <h1 style={{margin:0,fontSize:28,fontWeight:700,letterSpacing:-0.5}}><span style={{color:"#e85d24"}}>colour</span>count</h1>
        <p style={{margin:"4px 0 0",fontSize:13,color:"#999"}}>salon stock tracker</p>
      </div>

      <h2 style={{fontSize:18,fontWeight:600,marginBottom:20}}>{mode==="login"?"Log in":"Create account"}</h2>

      {mode==="signup"&&(
        <div style={{marginBottom:12}}>
          <label style={{fontSize:12,color:"#666",fontWeight:500,display:"block",marginBottom:4}}>Salon name</label>
          <input type="text" placeholder="e.g. Amy's Hair Studio" value={salonName} onChange={e=>setSalonName(e.target.value)} style={inp}/>
        </div>
      )}
      <div style={{marginBottom:12}}>
        <label style={{fontSize:12,color:"#666",fontWeight:500,display:"block",marginBottom:4}}>Email</label>
        <input type="email" placeholder="you@email.com" value={email} onChange={e=>setEmail(e.target.value)} style={inp}/>
      </div>
      <div style={{marginBottom:16}}>
        <label style={{fontSize:12,color:"#666",fontWeight:500,display:"block",marginBottom:4}}>Password</label>
        <input type="password" placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} style={inp}
          onKeyDown={e=>{if(e.key==="Enter")handleSubmit();}}/>
      </div>

      {error&&<p style={{color:"#dc2626",fontSize:13,margin:"0 0 12px"}}>{error}</p>}

      <button onClick={handleSubmit} disabled={loading}
        style={{width:"100%",padding:14,background:"#e85d24",color:"#fff",border:"none",borderRadius:10,fontSize:15,fontWeight:600,cursor:"pointer",fontFamily:"inherit",opacity:loading?0.6:1}}>
        {loading?"...":(mode==="login"?"Log in":"Sign up")}
      </button>

      <p style={{textAlign:"center",marginTop:20,fontSize:13,color:"#888"}}>
        {mode==="login"?"Don't have an account? ":"Already have an account? "}
        <button onClick={()=>{setMode(mode==="login"?"signup":"login");setError("");}}
          style={{background:"none",border:"none",color:"#e85d24",fontWeight:600,cursor:"pointer",fontFamily:"inherit",fontSize:13}}>
          {mode==="login"?"Sign up":"Log in"}
        </button>
      </p>
    </div>
  );
}

// ---- MAIN APP ----
const VIEWS = { USE:"use", STOCK:"stock", ORDER:"order", LOG:"log", EDIT:"edit" };

function MainApp({ token, salonName, onLogout }) {
  const [view, setView] = useState(VIEWS.USE);
  const [categories, setCategories] = useState([]);
  const [salon, setSalon] = useState(null);
  const [usageLog, setUsageLog] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState("list");
  const [editTarget, setEditTarget] = useState(null);
  const [formData, setFormData] = useState({});
  const [showSettings, setShowSettings] = useState(false);

  const a = useCallback((path, opts) => api(path, opts, token), [token]);

  async function reload() {
    try {
      const [s, c] = await Promise.all([a("/api/salon"), a("/api/categories")]);
      setSalon(s); setCategories(c);
    } catch {}
  }

  async function loadLog() { try { setUsageLog(await a("/api/usage?limit=200")); } catch {} }
  async function loadOrder() { try { setOrderItems(await a("/api/order")); } catch {} }

  useEffect(() => { reload().then(() => setLoading(false)); }, []);
  useEffect(() => { if(view===VIEWS.LOG) loadLog(); if(view===VIEWS.ORDER) loadOrder(); }, [view]);

  function showToast(m) { setToast(m); setTimeout(()=>setToast(null),2000); }

  const allProducts = categories.flatMap(c => c.products.map(p => ({...p, family: c.name, category_id: c.id})));
  const lowCount = allProducts.filter(p => p.stock_level > 0 && p.stock_level <= (salon?.reorder_threshold||1)).length;
  const outCount = allProducts.filter(p => p.stock_level === 0).length;

  function getStat(p) { return p.stock_level===0?"out":p.stock_level<=(salon?.reorder_threshold||1)?"low":"ok"; }

  async function logProduct(product, catName) {
    try {
      const r = await a(`/api/products/${product.id}/use`, {method:"POST"});
      if(r.is_out) showToast(`${product.code} ${product.name} — OUT OF STOCK`);
      else if(r.is_low) showToast(`${product.code} — low! (${r.stock_level} left)`);
      else showToast(`${product.code} — ${r.stock_level} left`);
      reload();
    } catch(e) { showToast(e.message); }
  }

  async function setStock(productId, level) {
    try { await a(`/api/products/${productId}/stock`,{method:"PATCH",body:JSON.stringify({stock_level:level})}); reload(); } catch {}
  }

  async function updateSalon(updates) {
    try { await a("/api/salon",{method:"PATCH",body:JSON.stringify(updates)}); reload(); } catch {}
  }

  async function addCategory(name) {
    try { await a("/api/categories",{method:"POST",body:JSON.stringify({name})}); reload(); showToast("Added"); } catch(e) { showToast(e.message); }
  }

  async function deleteCategory(id) {
    try { await a(`/api/categories/${id}`,{method:"DELETE"}); reload(); } catch {}
  }

  async function renameCategory(id, name) {
    try { await a(`/api/categories/${id}`,{method:"PATCH",body:JSON.stringify({name})}); reload(); } catch {}
  }

  async function addProduct(catId, product) {
    try { await a(`/api/categories/${catId}/products`,{method:"POST",body:JSON.stringify(product)}); reload(); showToast(`${product.name} added`); } catch(e) { showToast(e.message); }
  }

  async function deleteProduct(id) {
    try { await a(`/api/products/${id}`,{method:"DELETE"}); reload(); } catch {}
  }

  async function restockAll() {
    try { const r = await a("/api/restock",{method:"POST"}); showToast(`${r.restocked} products restocked`); reload(); loadOrder(); } catch {}
  }

  const filtered = searchTerm ? categories.map(c=>({...c,products:c.products.filter(p=>p.name.toLowerCase().includes(searchTerm.toLowerCase())||p.code.toLowerCase().includes(searchTerm.toLowerCase()))})).filter(c=>c.products.length>0) : categories;

  const inp = {width:"100%",padding:"10px 14px",border:"1px solid #ddd",borderRadius:10,fontSize:14,fontFamily:"inherit",outline:"none",boxSizing:"border-box",background:"#fafafa"};

  if(loading) return <div style={{display:"flex",justifyContent:"center",alignItems:"center",minHeight:300,fontFamily:"'DM Sans',sans-serif",background:"#ffffff"}}><p style={{color:"#888"}}>Loading...</p></div>;

  return (
    <div style={{fontFamily:"'DM Sans',sans-serif",maxWidth:768,margin:"0 auto",paddingBottom:80,color:"#1a1a1a",background:"#ffffff",colorScheme:"light",minHeight:"100vh"}}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet"/>

      {/* Header */}
      <div style={{padding:"20px 16px 12px",borderBottom:"1px solid #eee"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <h1 style={{margin:0,fontSize:22,fontWeight:700,letterSpacing:-0.5}}><span style={{color:"#e85d24"}}>colour</span>count</h1>
            <p style={{margin:"2px 0 0",fontSize:12,color:"#999",fontWeight:500}}>{salonName}</p>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {outCount>0&&<span style={{background:"#fee2e2",color:"#dc2626",fontSize:11,fontWeight:600,padding:"4px 10px",borderRadius:20}}>{outCount} out</span>}
            {lowCount>0&&<span style={{background:"#fef3c7",color:"#d97706",fontSize:11,fontWeight:600,padding:"4px 10px",borderRadius:20}}>{lowCount} low</span>}
            <button onClick={onLogout} style={{background:"none",border:"1px solid #ddd",borderRadius:8,padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",color:"#888"}}>Log out</button>
          </div>
        </div>
      </div>

      {/* Nav */}
      <div style={{display:"flex",borderBottom:"1px solid #eee",background:"#fafafa"}}>
        {[{key:VIEWS.USE,label:"Use",icon:"◉"},{key:VIEWS.STOCK,label:"Stock",icon:"◫"},{key:VIEWS.ORDER,label:"Order",icon:"↗"},{key:VIEWS.LOG,label:"Log",icon:"☰"},{key:VIEWS.EDIT,label:"Edit",icon:"✎"}].map(t=>(
          <button key={t.key} onClick={()=>{setView(t.key);setEditMode("list");setShowSettings(false);}}
            style={{flex:1,padding:"12px 0",border:"none",background:"none",fontSize:12,fontWeight:view===t.key?600:400,cursor:"pointer",color:view===t.key?"#e85d24":"#888",borderBottom:view===t.key?"2px solid #e85d24":"2px solid transparent",fontFamily:"inherit"}}>
            <span style={{fontSize:15,display:"block",marginBottom:1}}>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {toast&&<div style={{position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",background:"#1a1a1a",color:"#fff",padding:"10px 20px",borderRadius:10,fontSize:13,fontWeight:500,zIndex:999,fontFamily:"inherit",boxShadow:"0 4px 20px rgba(0,0,0,0.15)",maxWidth:340,textAlign:"center"}}>{toast}</div>}

      {/* USE */}
      {view===VIEWS.USE&&(
        <div style={{padding:16}}>
          <input type="text" placeholder="Search products..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} style={{...inp,marginBottom:16}}/>
          {filtered.length===0&&categories.length===0&&(
            <div style={{textAlign:"center",padding:40,color:"#aaa"}}>
              <p style={{fontSize:14,fontWeight:500}}>No products yet</p>
              <p style={{fontSize:12}}>Go to the Edit tab to add your colour catalogue</p>
            </div>
          )}
          {filtered.map(cat=>(
            <div key={cat.id} style={{marginBottom:20}}>
              <h3 style={{fontSize:12,fontWeight:600,color:"#999",textTransform:"uppercase",letterSpacing:1,margin:"0 0 8px"}}>{cat.name}</h3>
              {cat.products.length===0&&<p style={{fontSize:12,color:"#ccc",margin:"0 0 8px"}}>No products — add some in Edit tab</p>}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8}}>
                {cat.products.map(p=>{
                  const st=getStat(p);
                  return(
                    <button key={p.id} onClick={()=>logProduct(p,cat.name)}
                      onPointerDown={e=>{e.currentTarget.style.transform="scale(0.95)";}}
                      onPointerUp={e=>{e.currentTarget.style.transform="scale(1)";}}
                      onPointerLeave={e=>{e.currentTarget.style.transform="scale(1)";}}
                      style={{background:p.hex_colour,color:getContrastText(p.hex_colour),border:st==="out"?"2px dashed #dc2626":st==="low"?"2px solid #d97706":"1px solid rgba(0,0,0,0.08)",borderRadius:10,padding:"14px 10px",cursor:"pointer",textAlign:"left",fontFamily:"inherit",position:"relative",opacity:st==="out"?0.5:1,transition:"transform 0.1s"}}>
                      <div style={{fontSize:13,fontWeight:700}}>{p.code}</div>
                      <div style={{fontSize:10,fontWeight:500,marginTop:2,opacity:0.85,lineHeight:1.2}}>{p.name}</div>
                      <div style={{position:"absolute",top:6,right:6,background:st==="out"?"#dc2626":st==="low"?"#d97706":"rgba(0,0,0,0.25)",color:"#fff",fontSize:10,fontWeight:700,width:20,height:20,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center"}}>{p.stock_level}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* STOCK */}
      {view===VIEWS.STOCK&&(
        <div style={{padding:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <h2 style={{margin:0,fontSize:16,fontWeight:600}}>Stock levels</h2>
            <button onClick={()=>setShowSettings(!showSettings)} style={{padding:"6px 12px",border:"1px solid #ddd",borderRadius:8,background:"#fff",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Settings</button>
          </div>

          {showSettings&&salon&&(
            <div style={{background:"#f8f8f8",padding:16,borderRadius:12,marginBottom:16}}>
              {[{label:"Reorder alert below",key:"reorder_threshold",val:salon.reorder_threshold},{label:"Default stock per product",key:"default_stock",val:salon.default_stock}].map(({label,key,val})=>(
                <div key={key} style={{marginBottom:12}}>
                  <label style={{fontSize:12,color:"#666",fontWeight:500}}>{label}</label>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginTop:6}}>
                    <button onClick={()=>updateSalon({[key]:Math.max(1,val-1)})} style={{width:32,height:32,border:"1px solid #ddd",borderRadius:8,background:"#fff",fontSize:16,cursor:"pointer"}}>−</button>
                    <span style={{fontSize:18,fontWeight:600}}>{val}</span>
                    <button onClick={()=>updateSalon({[key]:val+1})} style={{width:32,height:32,border:"1px solid #ddd",borderRadius:8,background:"#fff",fontSize:16,cursor:"pointer"}}>+</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {allProducts.filter(p=>getStat(p)!=="ok").length>0&&(
            <div style={{marginBottom:20}}>
              <h3 style={{fontSize:12,fontWeight:600,color:"#dc2626",textTransform:"uppercase",letterSpacing:1,margin:"0 0 8px"}}>Needs attention</h3>
              {allProducts.filter(p=>getStat(p)!=="ok").sort((a,b)=>a.stock_level-b.stock_level).map(p=>{
                const st=getStat(p);
                return(
                  <div key={p.id} style={{display:"flex",alignItems:"center",padding:"10px 12px",border:"1px solid #eee",borderRadius:10,marginBottom:6,background:st==="out"?"#fef2f2":"#fffbeb"}}>
                    <div style={{width:32,height:32,borderRadius:8,background:p.hex_colour,marginRight:12,flexShrink:0}}/>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600}}>{p.code} — {p.name}</div>
                      <div style={{fontSize:11,color:"#888"}}>{p.family}</div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <button onClick={()=>setStock(p.id,Math.max(0,p.stock_level-1))} style={{width:28,height:28,border:"1px solid #ddd",borderRadius:6,background:"#fff",cursor:"pointer",fontSize:14}}>−</button>
                      <span style={{fontSize:16,fontWeight:700,minWidth:20,textAlign:"center",color:st==="out"?"#dc2626":"#d97706"}}>{p.stock_level}</span>
                      <button onClick={()=>setStock(p.id,p.stock_level+1)} style={{width:28,height:28,border:"1px solid #ddd",borderRadius:6,background:"#fff",cursor:"pointer",fontSize:14}}>+</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {categories.map(cat=>(
            <details key={cat.id} style={{marginBottom:8}}>
              <summary style={{fontSize:13,fontWeight:600,padding:"8px 0",cursor:"pointer",color:"#444"}}>{cat.name} <span style={{fontWeight:400,color:"#aaa",marginLeft:6}}>({cat.products.filter(p=>getStat(p)!=="ok").length} low)</span></summary>
              {cat.products.map(p=>(
                <div key={p.id} style={{display:"flex",alignItems:"center",padding:"8px 12px",borderBottom:"1px solid #f0f0f0"}}>
                  <div style={{width:24,height:24,borderRadius:6,background:p.hex_colour,marginRight:10,flexShrink:0}}/>
                  <div style={{flex:1}}><span style={{fontSize:13,fontWeight:500}}>{p.code}</span><span style={{fontSize:12,color:"#888",marginLeft:6}}>{p.name}</span></div>
                  <div style={{display:"flex",alignItems:"center",gap:4}}>
                    <button onClick={()=>setStock(p.id,Math.max(0,p.stock_level-1))} style={{width:24,height:24,border:"1px solid #ddd",borderRadius:4,background:"#fff",cursor:"pointer",fontSize:12}}>−</button>
                    <span style={{fontSize:14,fontWeight:600,minWidth:18,textAlign:"center"}}>{p.stock_level}</span>
                    <button onClick={()=>setStock(p.id,p.stock_level+1)} style={{width:24,height:24,border:"1px solid #ddd",borderRadius:4,background:"#fff",cursor:"pointer",fontSize:12}}>+</button>
                  </div>
                </div>
              ))}
            </details>
          ))}
        </div>
      )}

      {/* ORDER */}
      {view===VIEWS.ORDER&&(
        <div style={{padding:16}}>
          <h2 style={{margin:"0 0 4px",fontSize:16,fontWeight:600}}>Order list</h2>
          <p style={{margin:"0 0 16px",fontSize:12,color:"#888"}}>Products at or below {salon?.reorder_threshold||1} units</p>
          {orderItems.length===0?(
            <div style={{textAlign:"center",padding:40,color:"#aaa"}}><div style={{fontSize:32,marginBottom:8}}>✓</div><p style={{fontSize:14,fontWeight:500}}>Stock looking good</p></div>
          ):(
            <>
              <div style={{background:"#f8f8f8",padding:14,borderRadius:12,marginBottom:16}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{fontSize:13,color:"#666"}}>Products to reorder</span><span style={{fontSize:13,fontWeight:700}}>{orderItems.length}</span></div>
                <div style={{display:"flex",justifyContent:"space-between"}}><span style={{fontSize:13,color:"#666"}}>Total units needed</span><span style={{fontSize:13,fontWeight:700}}>{orderItems.reduce((s,i)=>s+i.to_order,0)}</span></div>
              </div>
              {[...new Set(orderItems.map(i=>i.category))].map(cat=>(
                <div key={cat} style={{marginBottom:16}}>
                  <h3 style={{fontSize:11,fontWeight:600,color:"#999",textTransform:"uppercase",letterSpacing:1,margin:"0 0 6px"}}>{cat}</h3>
                  {orderItems.filter(i=>i.category===cat).map(item=>(
                    <div key={item.id} style={{display:"flex",alignItems:"center",padding:"10px 12px",border:"1px solid #eee",borderRadius:10,marginBottom:6}}>
                      <div style={{width:32,height:32,borderRadius:8,background:item.hex_colour,marginRight:12,flexShrink:0}}/>
                      <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600}}>{item.code} — {item.name}</div></div>
                      <div style={{textAlign:"right"}}><div style={{fontSize:11,color:"#888"}}>have {item.stock_level}</div><div style={{fontSize:14,fontWeight:700,color:"#e85d24"}}>+{item.to_order}</div></div>
                    </div>
                  ))}
                </div>
              ))}
              <button onClick={()=>{const t="COLOUR ORDER\n"+"=".repeat(30)+"\n\n"+[...new Set(orderItems.map(i=>i.category))].map(f=>f.toUpperCase()+"\n"+orderItems.filter(i=>i.category===f).map(i=>`  ${i.code} ${i.name} — order ${i.to_order}`).join("\n")).join("\n\n")+"\n\nTotal: "+orderItems.reduce((s,i)=>s+i.to_order,0)+" units";navigator.clipboard.writeText(t).then(()=>showToast("Order copied!"));}}
                style={{width:"100%",padding:14,background:"#e85d24",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit",marginTop:8}}>Copy order list</button>
              <button onClick={restockAll}
                style={{width:"100%",padding:14,background:"#fff",color:"#e85d24",border:"2px solid #e85d24",borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit",marginTop:8}}>Mark all as restocked</button>
            </>
          )}
        </div>
      )}

      {/* LOG */}
      {view===VIEWS.LOG&&(
        <div style={{padding:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <h2 style={{margin:0,fontSize:16,fontWeight:600}}>Usage log</h2>
            <span style={{fontSize:12,color:"#888"}}>{usageLog.length} entries</span>
          </div>
          {usageLog.length===0?(
            <div style={{textAlign:"center",padding:40,color:"#aaa"}}><div style={{fontSize:32,marginBottom:8}}>◉</div><p style={{fontSize:14,fontWeight:500}}>No usage logged yet</p></div>
          ):usageLog.map((entry,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #f0f0f0"}}>
              <div style={{width:28,height:28,borderRadius:6,marginRight:10,flexShrink:0,background:allProducts.find(p=>p.code===entry.product_code)?.hex_colour||"#ccc"}}/>
              <div style={{flex:1}}><span style={{fontSize:13,fontWeight:500}}>{entry.product_code}</span><span style={{fontSize:12,color:"#888",marginLeft:6}}>{entry.product_name}</span></div>
              <span style={{fontSize:11,color:"#aaa"}}>{formatDate(entry.created_at)}</span>
            </div>
          ))}
        </div>
      )}

      {/* EDIT */}
      {view===VIEWS.EDIT&&(
        <div style={{padding:16}}>
          {editMode==="list"&&(
            <>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                <h2 style={{margin:0,fontSize:16,fontWeight:600}}>Edit products</h2>
                <button onClick={()=>{setEditMode("addFamily");setFormData({name:""});}}
                  style={{background:"#e85d24",color:"#fff",border:"none",borderRadius:10,padding:"10px 16px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>+ Category</button>
              </div>
              {categories.map((cat,ci)=>(
                <div key={cat.id} style={{border:"1px solid #eee",borderRadius:12,marginBottom:12,overflow:"hidden"}}>
                  <div style={{display:"flex",alignItems:"center",padding:"12px 14px",background:"#fafafa",gap:8}}>
                    <span style={{flex:1,fontSize:14,fontWeight:600}}>{cat.name}</span>
                    <span style={{fontSize:12,color:"#888"}}>{cat.products.length}</span>
                    <button onClick={()=>{setEditMode("editFamily");setEditTarget(cat.id);setFormData({name:cat.name});}}
                      style={{border:"none",background:"none",cursor:"pointer",fontSize:16,color:"#888"}}>✎</button>
                    <button onClick={()=>{if(window.confirm(`Delete "${cat.name}"?`))deleteCategory(cat.id);}}
                      style={{border:"none",background:"none",cursor:"pointer",fontSize:14,color:"#dc2626"}}>✕</button>
                  </div>
                  {cat.products.map(p=>(
                    <div key={p.id} style={{display:"flex",alignItems:"center",padding:"8px 14px",borderTop:"1px solid #f0f0f0"}}>
                      <div style={{width:24,height:24,borderRadius:6,background:p.hex_colour,marginRight:10,flexShrink:0,border:"1px solid rgba(0,0,0,0.08)"}}/>
                      <span style={{fontSize:13,fontWeight:500,marginRight:6}}>{p.code}</span>
                      <span style={{fontSize:12,color:"#888",flex:1}}>{p.name}</span>
                      <button onClick={()=>deleteProduct(p.id)} style={{border:"none",background:"none",cursor:"pointer",fontSize:12,color:"#dc2626"}}>✕</button>
                    </div>
                  ))}
                  <button onClick={()=>{setEditMode("addShade");setEditTarget(cat.id);setFormData({code:"",name:"",hex_colour:"#888888"});}}
                    style={{width:"100%",padding:10,border:"none",borderTop:"1px solid #f0f0f0",background:"none",cursor:"pointer",fontSize:13,fontWeight:500,color:"#e85d24",fontFamily:"inherit"}}>+ Add product</button>
                </div>
              ))}
            </>
          )}

          {editMode==="addFamily"&&(
            <div>
              <h2 style={{margin:"0 0 16px",fontSize:16,fontWeight:600}}>New category</h2>
              <input type="text" placeholder="e.g. Matrix Toners, Olaplex..." value={formData.name||""} onChange={e=>setFormData({...formData,name:e.target.value})} style={{...inp,marginBottom:16}} autoFocus/>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setEditMode("list")} style={{flex:1,padding:12,border:"1px solid #ddd",borderRadius:10,background:"#fff",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                <button onClick={()=>{if(formData.name?.trim()){addCategory(formData.name.trim());setEditMode("list");}}}
                  style={{flex:1,padding:12,background:"#e85d24",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Add</button>
              </div>
            </div>
          )}

          {editMode==="editFamily"&&editTarget!==null&&(
            <div>
              <h2 style={{margin:"0 0 16px",fontSize:16,fontWeight:600}}>Rename category</h2>
              <input type="text" value={formData.name||""} onChange={e=>setFormData({...formData,name:e.target.value})} style={{...inp,marginBottom:16}} autoFocus/>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setEditMode("list")} style={{flex:1,padding:12,border:"1px solid #ddd",borderRadius:10,background:"#fff",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                <button onClick={()=>{if(formData.name?.trim()){renameCategory(editTarget,formData.name.trim());setEditMode("list");}}}
                  style={{flex:1,padding:12,background:"#e85d24",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Save</button>
              </div>
            </div>
          )}

          {editMode==="addShade"&&editTarget!==null&&(
            <div>
              <h2 style={{margin:"0 0 4px",fontSize:16,fontWeight:600}}>Add product</h2>
              <p style={{margin:"0 0 16px",fontSize:12,color:"#888"}}>to {categories.find(c=>c.id===editTarget)?.name}</p>
              <label style={{fontSize:12,color:"#666",fontWeight:500,display:"block",marginBottom:4}}>Code / number</label>
              <input type="text" placeholder="e.g. 7.1, DEV-20" value={formData.code||""} onChange={e=>setFormData({...formData,code:e.target.value})} style={{...inp,marginBottom:12}} autoFocus/>
              <label style={{fontSize:12,color:"#666",fontWeight:500,display:"block",marginBottom:4}}>Name</label>
              <input type="text" placeholder="e.g. Medium Blonde Ash" value={formData.name||""} onChange={e=>setFormData({...formData,name:e.target.value})} style={{...inp,marginBottom:12}}/>
              <label style={{fontSize:12,color:"#666",fontWeight:500,display:"block",marginBottom:8}}>Colour</label>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
                {PRESET_COLOURS.map(c=>(
                  <button key={c} onClick={()=>setFormData({...formData,hex_colour:c})}
                    style={{width:30,height:30,borderRadius:8,background:c,border:formData.hex_colour===c?"3px solid #e85d24":"1px solid rgba(0,0,0,0.1)",cursor:"pointer",padding:0}}/>
                ))}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                <span style={{fontSize:12,color:"#666"}}>Custom hex:</span>
                <input type="text" value={formData.hex_colour||""} onChange={e=>setFormData({...formData,hex_colour:e.target.value})} style={{...inp,width:100}}/>
                <div style={{width:30,height:30,borderRadius:8,background:formData.hex_colour||"#888",border:"1px solid rgba(0,0,0,0.1)"}}/>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setEditMode("list")} style={{flex:1,padding:12,border:"1px solid #ddd",borderRadius:10,background:"#fff",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Done</button>
                <button onClick={()=>{
                  if(!formData.code?.trim()||!formData.name?.trim()){showToast("Code and name required");return;}
                  addProduct(editTarget,{code:formData.code.trim(),name:formData.name.trim(),hex_colour:formData.hex_colour||"#888888"});
                  setFormData({code:"",name:"",hex_colour:formData.hex_colour||"#888888"});
                }} style={{flex:1,padding:12,background:"#e85d24",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Add</button>
              </div>
              <p style={{fontSize:11,color:"#aaa",marginTop:8,textAlign:"center"}}>Keep adding — tap Done when finished</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---- ROOT COMPONENT ----
export default function ColourCount() {
  const [token, setToken] = useState(null);
  const [salonName, setSalonName] = useState("");

  function handleLogin(t, name) { setToken(t); setSalonName(name); }
  function handleLogout() { setToken(null); setSalonName(""); }

  if (!token) return <AuthScreen onLogin={handleLogin} />;
  return <MainApp token={token} salonName={salonName} onLogout={handleLogout} />;
}
